"use client";
import { Track } from "@/src/lib/tracking";

import { useCallback } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { safeStringifyError } from "utils";
import { invalidateChatDetailQueries } from "@utils/chatDetailInvalidateQueries";
import { openOnboardingPaywall } from "@components/ChatOnboarding/openOnboardingPaywall";
import type { SaveGoalArgs } from "@components/Chat/InteractiveTool/types";
import type { ConfirmSaveGoalResponse } from "@features/api/ChatApi";
import { QUERY_KEYS } from "@config/constant";
import { useConfirmSaveGoal } from "@/src/hooks/onboarding/useConfirmSaveGoal";
import { useMemberUser } from "@/src/hooks/useMemberUser";
import { useIsPc } from "@/src/hooks/useIsPc";
import { goalQueryKeys } from "@/src/hooks/goal/queryKeys";
import { planQueryKeys } from "@/src/hooks/plan/planQueryKeys";
import { resolveGoalIdForActivate } from "./resolveGoalIdForActivate";

export type GoalActivateRunResult =
  | { outcome: "dismissed" }
  | {
      outcome: "confirmed";
      goalId: string;
      status?: ConfirmSaveGoalResponse["status"];
    }
  | { outcome: "error"; error: unknown };

export type RunGoalActivateParams = {
  saveGoalArgs: SaveGoalArgs;
  goalId?: string;
  /** Chat 会话 id；confirm-save 成功后刷新详情 */
  chatId?: string;
};

function scheduleGoalPlanInvalidation(
  queryClient: ReturnType<typeof useQueryClient>,
): void {
  setTimeout(() => {
    void queryClient.invalidateQueries({ queryKey: goalQueryKeys.all });
    void queryClient.invalidateQueries({ queryKey: goalQueryKeys.stats() });
    queryClient.removeQueries({ queryKey: planQueryKeys.diagnosisGoals() });
    void queryClient.invalidateQueries({
      queryKey: [QUERY_KEYS.MEMBER_USE_PLAN],
    });
  }, 1000);
}

function syncChatDetailAfterActivate(
  queryClient: ReturnType<typeof useQueryClient>,
  chatId?: string,
): void {
  const trimmed = chatId?.trim();
  if (trimmed) {
    invalidateChatDetailQueries(queryClient, trimmed);
  }
}

/**
 * save_goal「Active my plan」激活 gate。
 *
 * paywall gate 在 confirm-save **之前**：非会员未付费 → 直接 dismissed，不调 confirm-save
 * （PLANNED 后端已废弃，旧逻辑"非会员可先建 PLANNED 草稿"不再成立，放行 confirm-save 会被后端拦）。
 * 付费成功 / 会员 → confirm-save 创建 ACTIVE goal，返回 goalId + status 供上层 append tool 消息让 Ed 承接。
 */
export function useGoalActivatePaywallGate() {
  const [isPC] = useIsPc();
  const queryClient = useQueryClient();
  const { data: memberPlanInfo } = useMemberUser();
  const isMember = memberPlanInfo?.isMember === true;
  const { mutateAsync: confirmSaveGoal, isPending: isConfirmSaving } =
    useConfirmSaveGoal();

  const openGoalActivatePaywall = useCallback(async () => {
    return openOnboardingPaywall(isPC, {
      paywallVariant: "goal_activate_second",
    });
  }, [isPC]);

  const resolveChatShouldActivate = useCallback(async (): Promise<boolean> => {
    if (isMember) {
      return true;
    }

    const paywallOutcome = await openGoalActivatePaywall();

    if (paywallOutcome === "purchased") {
      await queryClient.invalidateQueries({
        queryKey: [QUERY_KEYS.MEMBER_USE_PLAN],
      });
      return true;
    }

    return false;
  }, [isMember, openGoalActivatePaywall, queryClient]);

  const runActivate = useCallback(
    async (params: RunGoalActivateParams): Promise<GoalActivateRunResult> => {
      const resolvedGoalId = resolveGoalIdForActivate(
        params.saveGoalArgs,
        params.goalId,
      );

      const shouldActivate = await resolveChatShouldActivate();
      if (!shouldActivate) {
        return { outcome: "dismissed" };
      }

      let confirmResponse: ConfirmSaveGoalResponse | undefined;
      try {
        confirmResponse = await confirmSaveGoal({
          ...params.saveGoalArgs,
          ...(resolvedGoalId
            ? { goalId: resolvedGoalId }
            : { goalId: undefined }),
        });
      } catch (error) {
        Track.log("error", "GoalActivateGate", "confirm-save failed", {
          cause: String(safeStringifyError(error)),
        });
        return { outcome: "error", error };
      }

      if (!confirmResponse?.goalId) {
        return { outcome: "error", error: confirmResponse };
      }

      scheduleGoalPlanInvalidation(queryClient);

      const result = {
        outcome: "confirmed" as const,
        goalId: confirmResponse.goalId,
        status: confirmResponse.status,
      };
      syncChatDetailAfterActivate(queryClient, params.chatId);
      return result;
    },
    [confirmSaveGoal, queryClient, resolveChatShouldActivate],
  );

  return {
    runActivate,
    isActivating: isConfirmSaving,
    isMember,
  };
}
